/**
 * LIVE-OPS-001 — Live Operations (Today) detail view.
 * Lives inside Reports → Productivity Analytics. Role-aware payload from
 * GET /api/performance/operational-summary (processor vs team leader/admin).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  CheckCircle2, Inbox, MessageSquare, Wrench, ShieldCheck, Mail,
  Clock, Activity, AlertTriangle, Timer, RefreshCw, Flame, FileText, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { subscribe, refresh as refreshSummary } from '../../lib/operationalSummaryStore';
import UserDrilldownModal from './UserDrilldownModal';
import { AnKpiCard, AnAvatar } from './ui/AnalyticsUi';

const API = process.env.REACT_APP_BACKEND_URL || '';

// eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional browser-local live clock time
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—';
const fmtMins = (m) => m == null ? '—' : m >= 60 ? `${(m / 60).toFixed(1)}h` : `${Math.round(m)}m`;

const PRESENCE = {
  active: { label: 'Active', cls: 'bg-emerald-100 text-emerald-700' },
  on_break: { label: 'On Break', cls: 'bg-amber-100 text-amber-700' },
  offline: { label: 'Offline', cls: 'bg-slate-100 text-slate-500' },
};

const STATUS_CHIP = {
  done: { label: 'Target Met', cls: 'bg-emerald-100 text-emerald-700' },
  on_track: { label: 'On Track', cls: 'bg-blue-100 text-blue-700' },
  behind: { label: 'Behind', cls: 'bg-rose-100 text-rose-700' },
  no_target: { label: 'No Target', cls: 'bg-slate-100 text-slate-500' },
};

const ACCENT_TONE = { emerald: 'green', blue: 'blue', amber: 'amber', rose: 'red', violet: 'purple', cyan: 'cyan', orange: 'orange', sky: 'sky', slate: 'slate' };
// Presentation only — same value/label/sub inputs; tone derived from the previous accent classes
const KpiCard = ({ icon, label, value, accent = '', testId, sub }) => {
  const tone = ACCENT_TONE[(accent.match(/bg-([a-z]+)-50/) || [])[1]] || 'blue';
  const numeric = typeof value === 'number';
  return <AnKpiCard compact icon={icon} tone={tone} label={sub ? `${label} · ${sub}` : label} value={numeric ? value : undefined} display={numeric ? undefined : (value ?? '—')} testid={testId} />;
};

const ProgressBar = ({ percent, status }) => {
  const pct = Math.min(percent ?? 0, 100);
  const color = status === 'done' ? 'bg-emerald-500' : status === 'behind' ? 'bg-rose-500' : 'bg-blue-500';
  return (
    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const HourlyBars = ({ hourly, height = 44 }) => {
  if (!hourly?.length) return <p className="text-[11px] text-slate-400">No completions yet today</p>;
  const offset = new Date().getTimezoneOffset() / 60;
  const bars = hourly.map(h => ({ ...h, local: ((h.hour_utc - offset) % 24 + 24) % 24 }));
  const max = Math.max(...bars.map(b => b.count), 1);
  return (
    <div className="flex items-end gap-1" style={{ height }} data-testid="liveops-hourly-chart">
      {bars.map(b => (
        <div key={b.hour_utc} className="flex flex-col items-center gap-0.5" title={`${b.count} completed`}>
          <div className="w-5 bg-blue-500/80 rounded-sm min-h-[3px]" style={{ height: `${(b.count / max) * (height - 16)}px` }} />
          <span className="text-[9px] text-slate-400">{b.local % 12 || 12}{b.local < 12 ? 'a' : 'p'}</span>
        </div>
      ))}
    </div>
  );
};

export default function LiveOperations() {
  const [data, setData] = useState(null);
  const [myProgress, setMyProgress] = useState(null);
  const [drilldownUser, setDrilldownUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleSnapshot = async () => {
    setExporting(true);
    try {
      const res = await axios.get(
        `${API}/api/productivity-analytics/export/live-snapshot?tz_offset=${new Date().getTimezoneOffset()}`,
        { responseType: 'blob' });
      const match = (res.headers['content-disposition'] || '').match(/filename="([^"]+)"/);
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : 'PNA_Live_Operations.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Live snapshot PDF downloaded');
    } catch (e) {
      toast.error('Snapshot export failed — please try again');
    } finally {
      setExporting(false);
    }
  };

  // COMPLETION-PERF-A1 S4: shared 60 s poller with my-progress on the same tick;
  // manual refresh button triggers an immediate shared refresh.
  const fetchData = useCallback(() => refreshSummary(), []);

  useEffect(() => subscribe((s) => {
    setLoading(s.loading);
    if (s.data) setData(s.data);
    if (s.myProgress) setMyProgress(s.myProgress);
  }, { withMyProgress: true }), []);

  if (!data) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400 text-sm" data-testid="liveops-loading">
        Loading live operations…
      </div>
    );
  }
  const isLeader = data.role_view === 'leader';

  return (
    <div className="space-y-3" data-testid="live-operations">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
          </span>
          <h3 className="text-sm font-semibold text-slate-800">
            {isLeader ? 'Team Live Operations — Today' : 'My Live Operations — Today'}
          </h3>
          <span className="text-[11px] text-slate-400">
            Updated {fmtTime(data.generated_at)} · auto-refresh 30s
          </span>
        </div>
        <div className="flex items-center gap-1">
          {isLeader && (
            <button type="button" onClick={handleSnapshot} disabled={exporting}
              data-testid="liveops-snapshot-btn"
              className="an-btn flex items-center gap-1.5 text-xs font-semibold text-blue-700 border border-blue-200 bg-white rounded-lg px-3 h-8 hover:bg-blue-50 disabled:opacity-50">
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              Snapshot PDF
            </button>
          )}
          <button type="button" onClick={fetchData} data-testid="liveops-refresh-btn"
            className="an-btn h-8 w-8 flex items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-500">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      {isLeader
        ? <LeaderView data={data} onMemberClick={(m) => setDrilldownUser({ id: m.user_id, name: m.name })} />
        : <ProcessorView data={data} myProgress={myProgress} />}

      <UserDrilldownModal
        open={!!drilldownUser}
        onClose={() => setDrilldownUser(null)}
        userId={drilldownUser?.id}
        userName={drilldownUser?.name}
      />
    </div>
  );
}

const ProcessorView = ({ data, myProgress }) => {
  const t = data.target || {};
  const eta = data.estimated_finish || {};
  const chip = STATUS_CHIP[t.status] || STATUS_CHIP.on_track;
  const act = data.activity || {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard icon={CheckCircle2} label="Completed Today" value={data.completed_today} accent="bg-emerald-50 text-emerald-600" testId="liveops-kpi-completed" />
        <KpiCard icon={Inbox} label="My Remaining Queue" value={data.remaining_queue} accent="bg-blue-50 text-blue-600" testId="liveops-kpi-remaining" />
        <KpiCard icon={MessageSquare} label="Clarification" value={data.queues?.clarification ?? 0} accent="bg-amber-50 text-amber-600" testId="liveops-kpi-clarification" />
        <KpiCard icon={Wrench} label="Correction Required" value={data.queues?.correction ?? 0} accent="bg-rose-50 text-rose-600" testId="liveops-kpi-correction" />
        <KpiCard icon={ShieldCheck} label="QC Review" value={data.queues?.qc ?? 0} accent="bg-violet-50 text-violet-600" testId="liveops-kpi-qc" />
        <KpiCard icon={Mail} label="Mail-Away" value={data.queues?.mailaway ?? 0} accent="bg-cyan-50 text-cyan-600" testId="liveops-kpi-mailaway" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <div className="an-rise rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_-14px_rgba(15,23,42,.14)]" data-testid="liveops-target-card">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Today's Target</p>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${chip.cls}`}>{chip.label}</span>
          </div>
          <p className="text-lg font-bold text-slate-900 mb-1.5">
            {t.completed} / {t.value ?? '—'} {t.percent != null && <span className="text-sm font-medium text-slate-400">({t.percent}%)</span>}
          </p>
          <ProgressBar percent={t.percent} status={t.status} />
          {myProgress?.this_week?.target != null && (
            <div className="mt-2 pt-2 border-t border-slate-100" data-testid="liveops-weekly-progress">
              <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                <span>Week: <b className="text-slate-700">{myProgress.this_week.completed}</b> / {myProgress.this_week.target}</span>
                <span>{myProgress.this_week.days_remaining || 0}d left</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-violet-500 transition-all duration-500"
                  style={{ width: `${Math.min(myProgress.this_week.progress_percent ?? 0, 100)}%` }} />
              </div>
            </div>
          )}
          {myProgress?.streak?.current > 0 && (
            <div className="flex items-center gap-1 mt-1.5 text-[11px] font-semibold text-orange-600" data-testid="liveops-streak">
              <Flame className="w-3.5 h-3.5" /> {myProgress.streak.current}-day streak
            </div>
          )}
        </div>
        <div className="an-rise rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_-14px_rgba(15,23,42,.14)]" data-testid="liveops-eta-card">
          <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1"><Timer className="w-3 h-3" /> Estimated Finish</p>
          <p className="text-lg font-bold text-slate-900">
            {eta.status === 'target_met' ? '🎯 Target met' : eta.status === 'projected' ? `≈ ${fmtTime(eta.eta)}` : eta.status === 'no_target' ? 'No target set' : '—'}
          </p>
          <p className="text-[11px] text-slate-400">
            {eta.status === 'no_target' ? 'Targets are managed in Analytics → Performance Targets'
              : eta.pace_per_hour ? `${eta.pace_per_hour} files/hr pace` : 'Complete a file to project'}
            {data.avg_completion_minutes != null && ` · avg ${fmtMins(data.avg_completion_minutes)}/file`}
          </p>
        </div>
        <div className="an-rise rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_-14px_rgba(15,23,42,.14)]" data-testid="liveops-hourly-card">
          <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1"><Clock className="w-3 h-3" /> Hourly Completions</p>
          <HourlyBars hourly={data.hourly} />
        </div>
        <div className="an-rise rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_-14px_rgba(15,23,42,.14)]" data-testid="liveops-activity-card">
          <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1"><Activity className="w-3 h-3" /> My Activity Today</p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
            <span>Completed: <b>{act.files_completed ?? 0}</b></span>
            <span>Clarifications: <b>{act.clarifications_sent ?? 0}</b></span>
            <span>Corrections: <b>{act.corrections_received ?? 0}</b></span>
            <span>QC done: <b>{act.qc_completed ?? 0}</b></span>
            <span>Mail-Away: <b>{act.mailaway_created ?? 0}</b></span>
            <span className={act.errors_today ? 'text-rose-600' : ''}>Errors: <b>{act.errors_today ?? 0}</b></span>
          </div>
        </div>
      </div>
    </div>
  );
};

const LeaderView = ({ data, onMemberClick }) => {
  const team = data.team || {};
  const tgt = team.target || {};
  const sla = team.sla || {};
  const maxAssigned = Math.max(...(data.members || []).map(m => m.assigned), 1);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3">
        <KpiCard icon={CheckCircle2} label="Team Completed" value={team.completed_today ?? 0} accent="bg-emerald-50 text-emerald-600" testId="liveops-kpi-team-completed" />
        <KpiCard icon={Inbox} label="Team Remaining" value={team.remaining_queue ?? 0} accent="bg-blue-50 text-blue-600" testId="liveops-kpi-team-remaining" />
        <KpiCard icon={MessageSquare} label="Clarification" value={team.queues?.clarification ?? 0} accent="bg-amber-50 text-amber-600" testId="liveops-kpi-team-clarification" />
        <KpiCard icon={Mail} label="Mail-Away" value={team.queues?.mailaway ?? 0} accent="bg-cyan-50 text-cyan-600" testId="liveops-kpi-team-mailaway" />
        <KpiCard icon={ShieldCheck} label="QC Review" value={team.queues?.qc ?? 0} accent="bg-violet-50 text-violet-600" testId="liveops-kpi-team-qc" />
        <KpiCard icon={Clock} label="Avg Time" value={fmtMins(team.avg_completion_minutes)} accent="bg-slate-100 text-slate-600" testId="liveops-kpi-team-avgtime" />
        <KpiCard icon={AlertTriangle} label="SLA Health" value={`${sla.health_percent ?? 100}%`}
          sub={`${sla.overdue ?? 0} overdue · ${sla.due_today ?? 0} due today`}
          accent={(sla.overdue ?? 0) > 0 ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'} testId="liveops-kpi-team-sla" />
        <KpiCard icon={Timer} label="Avg Queue Age" value={team.avg_queue_age_hours != null ? `${team.avg_queue_age_hours}h` : '—'} accent="bg-slate-100 text-slate-600" testId="liveops-kpi-team-queueage" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="an-rise rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_-14px_rgba(15,23,42,.14)]" data-testid="liveops-team-target-card">
          <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1.5">Team Target Progress</p>
          <p className="text-lg font-bold text-slate-900 mb-1.5">
            {tgt.completed ?? 0} / {tgt.value ?? '—'} {tgt.percent != null && <span className="text-sm font-medium text-slate-400">({tgt.percent}%)</span>}
          </p>
          <ProgressBar percent={tgt.percent} status={tgt.percent >= 100 ? 'done' : tgt.percent >= 60 ? 'on_track' : 'behind'} />
        </div>
        <div className="an-rise rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_-14px_rgba(15,23,42,.14)]">
          <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1"><Clock className="w-3 h-3" /> Team Hourly Completions</p>
          <HourlyBars hourly={data.hourly} />
        </div>
      </div>

      {/* Team Performance Table (standard TitleFlow blue header via --orders-header-bg token) */}
      <div className="an-rise rounded-2xl border border-slate-200/80 bg-white overflow-x-auto shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_-14px_rgba(15,23,42,.14)]" data-testid="liveops-team-table">
        <table className="w-full text-xs">
          <thead className="an-thead">
            <tr className="text-left">
              <th className="px-3 py-2">Processor</th>
              <th className="px-2 py-2 text-center">Assigned</th>
              <th className="px-2 py-2 text-center">Completed</th>
              <th className="px-2 py-2 text-center">Remaining</th>
              <th className="px-2 py-2 text-center">Clarification</th>
              <th className="px-2 py-2 text-center">Mail-Away</th>
              <th className="px-2 py-2 text-center">QC</th>
              <th className="px-2 py-2 text-center">Avg Time</th>
              <th className="px-2 py-2 w-32">Target %</th>
              <th className="px-2 py-2">Workload</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {(data.members || []).map(m => {
              const p = PRESENCE[m.presence] || PRESENCE.offline;
              const pct = m.target_percent;
              const barCls = pct == null ? 'bg-slate-300' : pct >= 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-blue-500' : 'bg-rose-500';
              return (
                <tr key={m.user_id}
                  className="an-row border-b border-slate-100 cursor-pointer"
                  onClick={() => onMemberClick && onMemberClick(m)}
                  title="Click for performance details"
                  data-testid={`liveops-member-${m.user_id}`}>
                  <td className="px-3 py-1.5 font-medium text-slate-800 whitespace-nowrap"><span className="flex items-center gap-2"><AnAvatar name={m.name} tone="sky" />{m.name}</span></td>
                  <td className="px-2 py-1.5 text-center">{m.assigned}</td>
                  <td className="px-2 py-1.5 text-center font-semibold text-emerald-700">{m.completed_today}</td>
                  <td className="px-2 py-1.5 text-center">{m.remaining}</td>
                  <td className="px-2 py-1.5 text-center">{m.queues?.clarification ?? 0}</td>
                  <td className="px-2 py-1.5 text-center">{m.queues?.mailaway ?? 0}</td>
                  <td className="px-2 py-1.5 text-center">{m.queues?.qc ?? 0}</td>
                  <td className="px-2 py-1.5 text-center">{fmtMins(m.avg_completion_minutes)}</td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${barCls}`} style={{ width: `${Math.min(pct ?? 0, 100)}%` }} />
                      </div>
                      <span className="text-[10px] text-slate-500 w-8">{pct != null ? `${pct}%` : '—'}</span>
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" title={`${m.assigned} assigned`}>
                      <div className="h-full bg-indigo-400" style={{ width: `${(m.assigned / maxAssigned) * 100}%` }} />
                    </div>
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${p.cls}`}>{p.label}</span>
                  </td>
                </tr>
              );
            })}
            {(data.members || []).length === 0 && (
              <tr><td colSpan={11} className="text-center text-slate-400 py-4">No team members found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
